/* Canvas Galore — Per-slot photo uploader
 * Renders alongside cg-uploader.liquid.
 *
 * Pipeline per slot:
 *   1. Customer picks photo (file input).
 *   2. HEIC files are converted to JPEG in-browser (heic2any).
 *   3. Cropper.js opens with aspectRatio locked to √2 (A-series) in the
 *      chosen orientation.
 *   4. On confirm, getCroppedCanvas() → JPEG blob.
 *   5. If blob > 20 MB → downscale until it fits (Shopify line-item
 *      property file cap). Flag that we did so.
 *   6. Inject the resulting File back into the file input via DataTransfer
 *      so it gets POSTed natively by the product form.
 */
(function () {
  'use strict';

  const ROOT2     = Math.SQRT2;          // 1.41421
  const SIZE_CAP  = 20 * 1024 * 1024;    // Shopify per-file cap
  const QUALITY   = 0.92;
  const MAX_PX    = 5000;                // upper bound on cropped canvas

  // Min pixel on the short edge for a "comfortable" print at each size.
  // Below this we still allow upload, but warn the customer.
  const MIN_OK_PX = { A0: 2500, A1: 1800, A2: 1300, A3: 950, A4: 700, A5: 500 };

  // CDN libs we lazy-load on first interaction
  const CROPPER_CSS = 'https://cdn.jsdelivr.net/npm/cropperjs@1.6.1/dist/cropper.min.css';
  const CROPPER_JS  = 'https://cdn.jsdelivr.net/npm/cropperjs@1.6.1/dist/cropper.min.js';
  const HEIC_JS     = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';

  let cropper = null;
  let activeSlot = null;
  let activeOrient = 'portrait';

  function loadCss(href) {
    return new Promise(function (resolve) {
      if (document.querySelector('link[href="' + href + '"]')) return resolve();
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href; l.onload = resolve; l.onerror = resolve;
      document.head.appendChild(l);
    });
  }
  function loadJs(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) return resolve();
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = resolve; s.onerror = () => reject(new Error('Failed to load ' + src));
      document.head.appendChild(s);
    });
  }
  async function ensureCropper() {
    await loadCss(CROPPER_CSS);
    if (!window.Cropper) await loadJs(CROPPER_JS);
  }
  async function ensureHeic() {
    if (!window.heic2any) await loadJs(HEIC_JS);
  }

  function isHeic(file) {
    return /heic|heif/i.test(file.type) || /\.heic$|\.heif$/i.test(file.name);
  }
  async function maybeConvertHeic(file) {
    if (!isHeic(file)) return file;
    await ensureHeic();
    const blob = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
    return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
  }

  function aspectFor(orient) {
    return orient === 'landscape' ? ROOT2 : (1 / ROOT2);
  }

  function setSlotEmpty(slot) {
    slot.querySelector('[data-cg-slot-empty]').hidden = false;
    slot.querySelector('[data-cg-slot-filled]').hidden = true;
    const input = slot.querySelector('[data-cg-slot-input]');
    input.value = '';
    delete slot.dataset.objectUrl;
  }

  function setSlotFilled(slot, file, downscaled, sharpnessWarn) {
    slot.querySelector('[data-cg-slot-empty]').hidden = true;
    const filled = slot.querySelector('[data-cg-slot-filled]');
    filled.hidden = false;
    const thumbUrl = URL.createObjectURL(file);
    filled.querySelector('[data-cg-slot-thumb]').src = thumbUrl;
    filled.querySelector('[data-cg-slot-filename]').textContent =
      file.name + ' · ' + Math.round(file.size / 1024) + ' KB';
    const notice = filled.querySelector('[data-cg-slot-notice]');
    const msgs = [];
    if (downscaled) {
      msgs.push("We compressed this image to fit the upload limit. If you'd prefer to send the original, WhatsApp it to us with your order number.");
    }
    if (sharpnessWarn) {
      msgs.push("Heads up: this image may print slightly soft at " + slot.dataset.size + ". Use a higher-resolution photo if you have one.");
    }
    notice.textContent = msgs.join(' ');
    notice.hidden = msgs.length === 0;
    slot.dataset.objectUrl = thumbUrl;
  }

  function injectFileIntoInput(input, file) {
    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
  }

  async function downscaleIfNeeded(canvas, blob) {
    if (blob.size <= SIZE_CAP) return { blob: blob, downscaled: false };
    // Iteratively shrink until under cap
    let scale = Math.sqrt(SIZE_CAP / blob.size) * 0.95;
    let attempt = 0;
    while (blob.size > SIZE_CAP && attempt < 5) {
      const w = Math.max(800, Math.round(canvas.width * scale));
      const h = Math.max(800, Math.round(canvas.height * scale));
      const c2 = document.createElement('canvas');
      c2.width = w; c2.height = h;
      c2.getContext('2d').drawImage(canvas, 0, 0, w, h);
      blob = await new Promise(function (r) { c2.toBlob(r, 'image/jpeg', 0.88); });
      scale *= 0.85;
      attempt++;
    }
    return { blob: blob, downscaled: true };
  }

  async function openCropper(file, slot) {
    activeSlot = slot;
    activeOrient = slot.dataset.orient || 'portrait';
    // Reset modal orientation buttons to match the slot's current orient
    document.querySelectorAll('[data-cg-modal-orient]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-cg-modal-orient') === activeOrient);
    });
    await ensureCropper();
    const modal = document.querySelector('[data-cg-crop-modal]');
    const img = modal.querySelector('[data-cg-crop-img]');
    const loader = modal.querySelector('[data-cg-crop-loading]');
    loader.hidden = false;
    img.src = '';

    const converted = await maybeConvertHeic(file);
    slot._cgSourceFile = converted;
    const url = URL.createObjectURL(converted);
    img.src = url;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    img.onload = function () {
      loader.hidden = true;
      if (cropper) cropper.destroy();
      cropper = new window.Cropper(img, {
        aspectRatio: aspectFor(activeOrient),
        viewMode: 1,
        autoCropArea: 1,
        movable: true,
        zoomable: true,
        rotatable: false,
        scalable: false,
        background: false,
        responsive: true,
      });
    };
  }

  function closeCropper() {
    if (cropper) { cropper.destroy(); cropper = null; }
    activeSlot = null;
    const modal = document.querySelector('[data-cg-crop-modal]');
    modal.hidden = true;
    const img = modal.querySelector('[data-cg-crop-img]');
    if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
    img.src = '';
    document.body.style.overflow = '';
  }

  /* ---------- Event delegation ---------- */

  document.addEventListener('click', function (e) {
    // Toggle the whole panel
    const toggle = e.target.closest('[data-cg-uploader-toggle]');
    if (toggle) {
      const panel = toggle.parentElement.querySelector('[data-cg-uploader-panel]');
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.classList.toggle('is-open', open);
      const caret = toggle.querySelector('.cg-uploader__toggle-caret');
      if (caret) caret.textContent = open ? '−' : '+';
      return;
    }

    // Per-slot: open file picker
    const pick = e.target.closest('[data-cg-slot-pick]');
    if (pick) {
      pick.closest('[data-cg-slot]').querySelector('[data-cg-slot-input]').click();
      return;
    }

    // Per-slot: remove
    const remove = e.target.closest('[data-cg-slot-remove]');
    if (remove) {
      const slot = remove.closest('[data-cg-slot]');
      if (slot.dataset.objectUrl) URL.revokeObjectURL(slot.dataset.objectUrl);
      delete slot._cgSourceFile;
      setSlotEmpty(slot);
      return;
    }

    // Per-slot: re-crop existing file
    const recrop = e.target.closest('[data-cg-slot-recrop]');
    if (recrop) {
      const slot = recrop.closest('[data-cg-slot]');
      if (slot._cgSourceFile) openCropper(slot._cgSourceFile, slot);
      return;
    }

    // Per-slot: orientation toggle (LEGACY — kept for safety, no longer rendered)
    const orient = e.target.closest('[data-cg-orient]');
    if (orient) {
      const slot = orient.closest('[data-cg-slot]');
      slot.querySelectorAll('[data-cg-orient]').forEach(function (b) { b.classList.remove('is-active'); });
      orient.classList.add('is-active');
      slot.dataset.orient = orient.getAttribute('data-cg-orient');
      if (slot._cgSourceFile) openCropper(slot._cgSourceFile, slot);
      return;
    }

    // Modal-level orientation toggle — flips cropper aspect ratio live, no reload
    const modalOrient = e.target.closest('[data-cg-modal-orient]');
    if (modalOrient) {
      const next = modalOrient.getAttribute('data-cg-modal-orient');
      document.querySelectorAll('[data-cg-modal-orient]').forEach(function (b) {
        b.classList.toggle('is-active', b === modalOrient);
      });
      activeOrient = next;
      if (activeSlot) activeSlot.dataset.orient = next;
      if (cropper) cropper.setAspectRatio(aspectFor(next));
      return;
    }

    // Crop modal: cancel
    if (e.target.closest('[data-cg-crop-cancel]')) {
      closeCropper();
      return;
    }

    // Crop modal: confirm
    if (e.target.closest('[data-cg-crop-confirm]')) {
      confirmCrop().catch(function (err) {
        alert('Could not save crop: ' + err.message);
      });
      return;
    }
  });

  document.addEventListener('change', async function (e) {
    const input = e.target.closest('[data-cg-slot-input]');
    if (!input) return;
    const file = input.files[0];
    if (!file) return;
    const slot = input.closest('[data-cg-slot]');
    try {
      await openCropper(file, slot);
    } catch (err) {
      alert('Could not load that image: ' + err.message);
    }
  });

  async function confirmCrop() {
    if (!cropper || !activeSlot) return;
    const slot = activeSlot;
    const canvas = cropper.getCroppedCanvas({
      maxWidth:  MAX_PX,
      maxHeight: MAX_PX,
      fillColor: '#ffffff',
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });
    if (!canvas) throw new Error('Crop failed.');
    let blob = await new Promise(function (r) { canvas.toBlob(r, 'image/jpeg', QUALITY); });
    if (!blob) throw new Error('Could not encode image.');

    const ds = await downscaleIfNeeded(canvas, blob);
    blob = ds.blob;

    const minSide = Math.min(canvas.width, canvas.height);
    const need = MIN_OK_PX[slot.dataset.size] || 700;
    const sharpnessWarn = minSide < need;

    const srcName = (slot._cgSourceFile && slot._cgSourceFile.name) || 'photo.jpg';
    const filename = srcName.replace(/\.\w+$/, '') + '.jpg';
    const file = new File([blob], filename, { type: 'image/jpeg' });
    injectFileIntoInput(slot.querySelector('[data-cg-slot-input]'), file);
    setSlotFilled(slot, file, ds.downscaled, sharpnessWarn);
    closeCropper();
  }
})();
